// src/views/contasPagar/ContaPagarModal.jsx
import React, { useState, useEffect, useCallback } from "react";
import fornecedoresService from "../../services/fornecedoresService";
import contasContabeisService from "../../services/contasContabeisService";
import contasBancariasService from "../../services/contasBancariasService";

const fmtMoeda = v => isNaN(parseFloat(v))?"R$ 0,00":parseFloat(v).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const sanDate  = v => {if(!v||v==="")return"";if(typeof v==="string"&&v.includes("T"))return v.split("T")[0];return v;};

const EMPTY = {
  tpDocumento:"NotaFiscal",nrDocumento:"",serie:"",dtEmissao:"",dtCompetencia:"",
  descricao:"",idFornecedor:"",idContaContabil:"",
  vlrBruto:"0.00",vlrDesconto:"0.00",
  vlrInss:"0.00",vlrIss:"0.00",vlrIr:"0.00",vlrPis:"0.00",vlrCofins:"0.00",vlrCsll:"0.00",
  idCtInss:"",idCtIss:"",idCtIr:"",idCtPis:"",idCtCofins:"",idCtCsll:"",
  ehRateavel:false,tpRateio:"FracaoIdeal",observacao:"",
  anexoBase64:"",
};

// Divide o valor líquido em N parcelas iguais. A última absorve a diferença
// dos centavos perdidos por arredondamento (ex.: R$ 100/3 → 33,33 + 33,33 + 33,34).
function gerarParcelas(vlrLiquido, nrParcelas, dtPrimeiro) {
  const arr  = [];
  const n    = Math.max(1, parseInt(nrParcelas) || 1);
  const totalCentavos = Math.round(parseFloat(vlrLiquido||0) * 100);
  const baseCentavos  = Math.floor(totalCentavos / n);
  const resto         = totalCentavos - baseCentavos * n;
  for (let i = 0; i < n; i++) {
    const d = new Date(dtPrimeiro); d.setMonth(d.getMonth() + i);
    // A última parcela ganha o resto para garantir soma exata.
    const cents = baseCentavos + (i === n - 1 ? resto : 0);
    arr.push({
      dtVencimento: d.toISOString().split("T")[0],
      vlrParcela:   (cents / 100).toFixed(2),
      idContaBancaria: "",
    });
  }
  return arr;
}

export default function ContaPagarModal({show,onClose,onSave,editData,saving=false,idCondominio,condominioNome=""}){
  const isEdit=!!editData?.id;
  const [form,setForm]=useState(EMPTY);
  const [errors,setErrors]=useState({});
  const [tab,setTab]=useState("dados");
  const [fornecedores,setFornecedores]=useState([]);
  const [contasContabeis,setContasContabeis]=useState([]);
  const [contasBancarias,setContasBancarias]=useState([]);
  const [parcelas,setParcelas]=useState([{dtVencimento:"",vlrParcela:"",idContaBancaria:""}]);
  const [nrParcelas,setNrParcelas]=useState("1");
  const [dtPrimeiro,setDtPrimeiro]=useState("");

  useEffect(()=>{
    if(!idCondominio)return;
    fornecedoresService.listar({idCondominio,situacao:"ativo",limit:200}).then(r=>setFornecedores(r.data||[])).catch(()=>{});
    contasContabeisService.listar({idCondominio,situacao:"ativo",natureza:"Analitica"}).then(r=>setContasContabeis(r.data||[])).catch(()=>{});
    contasBancariasService.listar({idCondominio,situacao:"ativo"}).then(r=>setContasBancarias(r.data||[])).catch(()=>{});
  },[idCondominio]);

  useEffect(()=>{
    if(!show)return;
    if(editData){
      const base={...EMPTY,...editData};
      ["dtEmissao","dtCompetencia"].forEach(f=>{base[f]=sanDate(base[f]);});
      setForm(base);setParcelas(editData.parcelas||[{dtVencimento:"",vlrParcela:"",idContaBancaria:""}]);
    } else {setForm({...EMPTY});setParcelas([{dtVencimento:"",vlrParcela:"",idContaBancaria:""}]);}
    setErrors({});setTab("dados");setNrParcelas("1");setDtPrimeiro("");
  },[show,editData]);

  const set=(f,v)=>{setForm(p=>({...p,[f]:v}));setErrors(p=>({...p,[f]:undefined}));};

  const vlrLiquido=useCallback(()=>{
    const n=k=>parseFloat(form[k]||0);
    return Math.max(0,n("vlrBruto")-n("vlrDesconto")-n("vlrInss")-n("vlrIss")-n("vlrIr")-n("vlrPis")-n("vlrCofins")-n("vlrCsll")).toFixed(2);
  },[form]);

  const totalRetencoes=()=>(["vlrInss","vlrIss","vlrIr","vlrPis","vlrCofins","vlrCsll"].reduce((a,k)=>a+parseFloat(form[k]||0),0)).toFixed(2);

  const handleGerarParcelas=()=>{
    if(!dtPrimeiro)return alert("Informe a data do primeiro vencimento.");
    setParcelas(gerarParcelas(vlrLiquido(),nrParcelas,dtPrimeiro));
  };

  // Soma das parcelas em centavos (evita erros de ponto flutuante)
  const somaParcelasCents = () => parcelas.reduce(
    (a, p) => a + Math.round(parseFloat(p.vlrParcela||0) * 100), 0
  );
  const liquidoCents = () => Math.round(parseFloat(vlrLiquido()) * 100);
  const diferencaParcelas = () => (somaParcelasCents() - liquidoCents()) / 100;

  const validate = () => {
    const e = {};
    if (!form.descricao.trim()) e.descricao = "Descrição é obrigatória";
    if (parseFloat(form.vlrBruto||0) <= 0) e.vlrBruto = "Valor bruto deve ser maior que zero";
    if (!parcelas.length || !parcelas[0].dtVencimento)
      e.parcelas = "Informe ao menos uma parcela com vencimento";
    // Verifica se todas as parcelas têm vencimento e valor positivo
    if (!e.parcelas) {
      const semData = parcelas.some(p => !p.dtVencimento);
      const semVlr  = parcelas.some(p => !p.vlrParcela || parseFloat(p.vlrParcela) <= 0);
      if (semData) e.parcelas = "Todas as parcelas precisam de data de vencimento";
      else if (semVlr)  e.parcelas = "Todas as parcelas precisam de valor maior que zero";
    }
    // Soma das parcelas precisa bater com o líquido (tolera até 1 centavo)
    if (!e.parcelas && Math.abs(somaParcelasCents() - liquidoCents()) > 1) {
      const diff = diferencaParcelas();
      e.parcelas = `Soma das parcelas (R$ ${(somaParcelasCents()/100).toFixed(2)}) difere do valor líquido em ${diff>0?"+":""}${diff.toFixed(2)}. Ajuste antes de salvar.`;
    }
    setErrors(e);
    if (Object.keys(e).length) {
      if (e.parcelas) setTab("parcelas"); else setTab("dados");
      return false;
    }
    return true;
  };

  // Aplica a mesma conta bancária a todas as parcelas (atalho ao gerar).
  const aplicarContaATodas = (idContaBancaria) => {
    setParcelas(prev => prev.map(p => ({...p, idContaBancaria})));
  };

  // Upload de anexo: aceita PDF/imagem até ~5MB e armazena em base64.
  const handleAnexo = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Arquivo muito grande. Limite: 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => set("anexoBase64", ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = () => { if (validate()) onSave({ ...form, parcelas, vlrLiquido: vlrLiquido(), id: editData?.id }); };
  if (!show) return null;

  const contasDespesa=contasContabeis.filter(c=>["DespesaAdministrativa","DespesaOperacional","DespesaFinanceira","FundoReserva","Outro"].includes(c.tpConta));
  const contasPassivo=contasContabeis.filter(c=>["PassivoCirculante","PassivoNaoCirculante"].includes(c.tpConta));

  const tabs=[
    {id:"dados",label:"Documento",icon:"bi-file-text"},
    {id:"retencoes",label:"Retenções",icon:"bi-percent"},
    {id:"parcelas",label:"Parcelas",icon:"bi-calendar3"},
  ];

  return(
    <div className="modal fade show d-block" style={{background:"rgba(0,0,0,.55)"}} tabIndex="-1">
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div className="d-flex align-items-center gap-3">
              <div style={{width:38,height:38,background:"rgba(255,255,255,.15)",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center"}}><i className="bi bi-receipt" style={{fontSize:18,color:"#fff"}}/></div>
              <div><h5 className="modal-title mb-0">{isEdit?"Editar Conta a Pagar":"Nova Conta a Pagar"}</h5><small style={{color:"rgba(255,255,255,.6)",fontSize:12}}>{condominioNome}</small></div>
            </div>
            <button className="btn-close btn-close-white" onClick={onClose}/>
          </div>
          <div style={{background:"var(--primary)",padding:"0 24px"}}>
            <ul className="nav nav-tabs border-0" style={{gap:4}}>
              {tabs.map(t=>(
                <li key={t.id} className="nav-item">
                  <button className={`nav-link border-0 d-flex align-items-center gap-2 ${tab===t.id?"active":""}`}
                    style={{background:tab===t.id?"#fff":"transparent",color:tab===t.id?"var(--primary)":"rgba(255,255,255,.7)",borderRadius:"6px 6px 0 0",fontFamily:"Poppins,sans-serif",fontSize:13,fontWeight:500,padding:"10px 18px"}}
                    onClick={()=>setTab(t.id)}>
                    <i className={`bi ${t.icon}`}/>{t.label}
                    {t.id==="parcelas"&&errors.parcelas&&<span style={{background:"#ef4444",color:"#fff",borderRadius:10,padding:"0 5px",fontSize:10}}>!</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="modal-body">
            {tab==="dados"&&(
              <>
                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-file-text"/> Documento</div>
                  <div className="row g-3">
                    <div className="col-md-3"><label className="form-label">Tipo</label>
                      <select className="form-select" value={form.tpDocumento} onChange={e=>set("tpDocumento",e.target.value)}>
                        {["NotaFiscal","Fatura","Recibo","Contrato","Folha","Outro"].map(t=><option key={t} value={t}>{t.replace(/([A-Z])/g," $1").trim()}</option>)}
                      </select>
                    </div>
                    <div className="col-md-2"><label className="form-label">Nº Documento</label><input className="form-control" value={form.nrDocumento} onChange={e=>set("nrDocumento",e.target.value)} placeholder="NF-001"/></div>
                    <div className="col-md-1"><label className="form-label">Série</label><input className="form-control" value={form.serie} onChange={e=>set("serie",e.target.value)}/></div>
                    <div className="col-md-3"><label className="form-label">Data de Emissão</label><input type="date" className="form-control" value={form.dtEmissao} onChange={e=>set("dtEmissao",e.target.value)}/></div>
                    <div className="col-md-3"><label className="form-label">Competência</label><input type="date" className="form-control" value={form.dtCompetencia} onChange={e=>set("dtCompetencia",e.target.value)}/></div>
                    <div className="col-md-8">
                      <label className="form-label">Descrição <span className="required-mark">*</span></label>
                      <input className={`form-control ${errors.descricao?"is-invalid":""}`} value={form.descricao} onChange={e=>set("descricao",e.target.value)} placeholder="Descrição da despesa"/>
                      {errors.descricao&&<div className="invalid-feedback">{errors.descricao}</div>}
                    </div>
                    <div className="col-md-4"><label className="form-label">Fornecedor / Prestador</label>
                      <select className="form-select" value={form.idFornecedor} onChange={e=>set("idFornecedor",e.target.value)}>
                        <option value="">— Selecione —</option>
                        {fornecedores.map(f=><option key={f.id} value={f.id}>{f.nmReduzido||f.nmRazaoSocial}</option>)}
                      </select>
                    </div>
                    <div className="col-md-5"><label className="form-label">Conta Contábil (Despesa)</label>
                      <select className="form-select" value={form.idContaContabil} onChange={e=>set("idContaContabil",e.target.value)}>
                        <option value="">— Selecione —</option>
                        {contasDespesa.map(c=><option key={c.id} value={c.id}>{c.classificacaoContabil} — {c.descConta}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-cash-coin"/> Valores</div>
                  <div className="row g-3">
                    <div className="col-md-3"><label className="form-label">Valor Bruto <span className="required-mark">*</span></label>
                      <div className="input-group"><span className="input-group-text">R$</span>
                        <input className={`form-control ${errors.vlrBruto?"is-invalid":""}`} type="number" step="0.01" min="0" value={form.vlrBruto} onChange={e=>set("vlrBruto",e.target.value)}/>
                        {errors.vlrBruto&&<div className="invalid-feedback">{errors.vlrBruto}</div>}
                      </div>
                    </div>
                    <div className="col-md-3"><label className="form-label">Desconto</label>
                      <div className="input-group"><span className="input-group-text">R$</span><input className="form-control" type="number" step="0.01" min="0" value={form.vlrDesconto} onChange={e=>set("vlrDesconto",e.target.value)}/></div>
                    </div>
                    <div className="col-md-3"><label className="form-label">Total Retenções</label>
                      <div className="input-group"><span className="input-group-text">R$</span><input className="form-control" readOnly value={totalRetencoes()} style={{background:"var(--gray-50)"}}/></div>
                    </div>
                    <div className="col-md-3"><label className="form-label">Valor Líquido</label>
                      <div className="input-group"><span className="input-group-text" style={{background:"var(--bg-success-soft)",color:"#166534",fontWeight:700}}>R$</span>
                        <input className="form-control" readOnly value={vlrLiquido()} style={{fontWeight:700,color:"#166534",background:"var(--bg-success-soft)"}}/>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-diagram-3"/> Rateio</div>
                  <div className="row g-3 align-items-center">
                    <div className="col-md-3">
                      <div className="form-check form-switch">
                        <input className="form-check-input" type="checkbox" id="ehRateavel" checked={form.ehRateavel} onChange={e=>set("ehRateavel",e.target.checked)}/>
                        <label className="form-check-label" htmlFor="ehRateavel" style={{fontSize:13,fontWeight:600,color:"var(--gray-600)"}}>É rateável entre unidades</label>
                      </div>
                    </div>
                    {form.ehRateavel&&(
                      <div className="col-md-4"><label className="form-label">Tipo de Rateio</label>
                        <select className="form-select" value={form.tpRateio} onChange={e=>set("tpRateio",e.target.value)}>
                          <option value="FracaoIdeal">Por Fração Ideal</option>
                          <option value="Igualitario">Igualitário (por unidade)</option>
                          <option value="ValorFixo">Valor Fixo por Unidade</option>
                        </select>
                      </div>
                    )}
                    <div className="col-12"><label className="form-label">Observações</label>
                      <textarea className="form-control" rows={2} value={form.observacao} onChange={e=>set("observacao",e.target.value)}/>
                    </div>
                  </div>
                </div>

                {/* Anexo (NF, boleto, comprovante) */}
                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-paperclip"/> Anexo (NF / Boleto)</div>
                  {form.anexoBase64 ? (
                    <div style={{background:"var(--bg-success-soft)",border:"1px solid #BBF7D0",borderRadius:8,
                      padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                      <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                        <i className="bi bi-file-earmark-check-fill" style={{fontSize:22,color:"#166534"}}/>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:600,color:"#166534"}}>Anexo carregado</div>
                          <div style={{fontSize:11,color:"var(--gray-500)"}}>
                            {Math.round((form.anexoBase64.length * 0.75) / 1024)} KB · {form.anexoBase64.substring(5,20)}
                          </div>
                        </div>
                      </div>
                      <div style={{display:"flex",gap:6}}>
                        <a className="btn btn-outline-secondary btn-sm" href={form.anexoBase64} target="_blank" rel="noreferrer">
                          <i className="bi bi-eye"/> Ver
                        </a>
                        <button type="button" className="btn btn-outline-danger btn-sm"
                          onClick={()=>set("anexoBase64","")}>
                          <i className="bi bi-trash"/> Remover
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{border:"2px dashed var(--gray-200)",borderRadius:8,padding:"20px",
                      textAlign:"center",background:"var(--gray-50)"}}>
                      <i className="bi bi-cloud-upload" style={{fontSize:30,color:"var(--gray-400)",display:"block",marginBottom:6}}/>
                      <div style={{fontSize:13,color:"var(--gray-500)",marginBottom:8}}>
                        Anexe a Nota Fiscal, boleto ou comprovante (PDF, JPG ou PNG até 5MB)
                      </div>
                      <label className="btn btn-outline-primary btn-sm" style={{cursor:"pointer"}}>
                        <i className="bi bi-paperclip me-1"/>Escolher arquivo
                        <input type="file" accept=".pdf,image/*" style={{display:"none"}}
                          onChange={e=>handleAnexo(e.target.files?.[0])}/>
                      </label>
                    </div>
                  )}
                </div>
              </>
            )}

            {tab==="retencoes"&&(
              <div className="form-section">
                <div className="form-section-title"><i className="bi bi-percent"/> Retenções</div>
                <div style={{background:"var(--bg-highlight)",border:"1px solid #BFDBFE",borderRadius:8,padding:"10px 14px",fontSize:12.5,color:"var(--primary)",marginBottom:16}}>
                  <i className="bi bi-info-circle me-2"/>Cada retenção deduz o valor líquido e é lançada em conta contábil de passivo para recolhimento posterior.
                </div>
                <div className="row g-3">
                  {[{l:"INSS",v:"vlrInss",c:"idCtInss"},{l:"ISS",v:"vlrIss",c:"idCtIss"},{l:"IR",v:"vlrIr",c:"idCtIr"},{l:"PIS",v:"vlrPis",c:"idCtPis"},{l:"COFINS",v:"vlrCofins",c:"idCtCofins"},{l:"CSLL",v:"vlrCsll",c:"idCtCsll"}].map(({l,v,c})=>(
                    <div className="col-md-6" key={l}>
                      <div style={{background:"var(--gray-50)",border:"1px solid var(--gray-200)",borderRadius:8,padding:"12px 14px"}}>
                        <div style={{fontWeight:700,fontSize:13,color:"var(--gray-700)",marginBottom:8}}>{l}</div>
                        <div className="row g-2">
                          <div className="col-5"><label className="form-label" style={{fontSize:11.5}}>Valor (R$)</label>
                            <div className="input-group input-group-sm"><span className="input-group-text">R$</span><input className="form-control" type="number" step="0.01" min="0" value={form[v]} onChange={e=>set(v,e.target.value)}/></div>
                          </div>
                          <div className="col-7"><label className="form-label" style={{fontSize:11.5}}>Conta Passivo</label>
                            <select className="form-select form-select-sm" value={form[c]} onChange={e=>set(c,e.target.value)}>
                              <option value="">— Selecione —</option>
                              {contasPassivo.map(ct=><option key={ct.id} value={ct.id}>{ct.classificacaoContabil} — {ct.descConta}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="col-12">
                    <div style={{background:"var(--bg-success-soft)",border:"1px solid #BBF7D0",borderRadius:8,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <span style={{fontSize:13,color:"var(--gray-600)"}}>Total Retenções:</span>
                      <span style={{fontWeight:700,fontSize:16,color:"#166534"}}>{fmtMoeda(totalRetencoes())}</span>
                    </div>
                    <div style={{background:"var(--bg-highlight)",borderRadius:8,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8}}>
                      <span style={{fontSize:13,color:"var(--gray-600)"}}>Valor Líquido (a pagar ao fornecedor):</span>
                      <span style={{fontWeight:700,fontSize:18,color:"var(--primary)"}}>{fmtMoeda(vlrLiquido())}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {tab==="parcelas"&&(
              <div className="form-section">
                <div className="form-section-title d-flex justify-content-between align-items-center">
                  <span><i className="bi bi-calendar3"/> Parcelas</span>
                  <span style={{fontSize:13,color:"var(--primary)",fontWeight:700}}>Líquido: {fmtMoeda(vlrLiquido())}</span>
                </div>
                {!isEdit&&(
                  <div style={{background:"var(--bg-success-soft)",border:"1px solid #BBF7D0",borderRadius:8,padding:"14px 16px",marginBottom:16}}>
                    <div style={{fontWeight:600,fontSize:13,color:"#166534",marginBottom:10}}><i className="bi bi-lightning-charge me-2"/>Gerar parcelas automaticamente</div>
                    <div className="row g-2 align-items-end">
                      <div className="col-md-2"><label className="form-label" style={{fontSize:12}}>Nº Parcelas</label>
                        <input className="form-control form-control-sm" type="number" min="1" max="60" value={nrParcelas} onChange={e=>setNrParcelas(e.target.value)}/>
                      </div>
                      <div className="col-md-3"><label className="form-label" style={{fontSize:12}}>1º Vencimento</label>
                        <input type="date" className="form-control form-control-sm" value={dtPrimeiro} onChange={e=>setDtPrimeiro(e.target.value)}/>
                      </div>
                      <div className="col-md-3"><label className="form-label" style={{fontSize:12}}>Valor por Parcela</label>
                        <input className="form-control form-control-sm" readOnly value={`R$ ${(parseFloat(vlrLiquido())/parseInt(nrParcelas||1)).toFixed(2)}`} style={{background:"var(--gray-50)"}}/>
                      </div>
                      <div className="col-md-2">
                        <button className="btn btn-success w-100 btn-sm" onClick={handleGerarParcelas}><i className="bi bi-magic"/> Gerar</button>
                      </div>
                    </div>
                    <div style={{fontSize:11,color:"#166534",marginTop:6}}>
                      <i className="bi bi-info-circle me-1"/>A última parcela absorve diferenças de centavos para garantir soma exata.
                    </div>
                  </div>
                )}

                {/* Atalho: aplicar a mesma conta bancária a todas as parcelas */}
                {!isEdit && parcelas.length > 1 && (
                  <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:12,
                    background:"var(--bg-highlight)",border:"1px solid #BFDBFE",borderRadius:8,padding:"8px 12px"}}>
                    <i className="bi bi-bank" style={{color:"var(--primary)"}}/>
                    <span style={{fontSize:12,color:"var(--gray-600)"}}>Aplicar mesma conta bancária a todas:</span>
                    <select className="form-select form-select-sm" style={{maxWidth:260}}
                      onChange={e=>{ if(e.target.value) aplicarContaATodas(e.target.value); e.target.value=""; }}>
                      <option value="">— Selecione para aplicar —</option>
                      {contasBancarias.map(c => <option key={c.id} value={c.id}>{c.nmConta}</option>)}
                    </select>
                  </div>
                )}

                {/* Indicador de diferença soma vs líquido */}
                {parcelas.length > 0 && Math.abs(diferencaParcelas()) > 0.01 && (
                  <div style={{background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:8,
                    padding:"10px 14px",marginBottom:12,fontSize:13,color:"#92400E",
                    display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span>
                      <i className="bi bi-exclamation-triangle me-1"/>
                      Soma das parcelas: <strong>{fmtMoeda(somaParcelasCents()/100)}</strong> —
                      Diferença: <strong>{(diferencaParcelas()>0?"+":"")}{fmtMoeda(Math.abs(diferencaParcelas()))}</strong>
                    </span>
                    <button type="button" className="btn btn-outline-warning btn-sm"
                      onClick={()=>{
                        // Ajusta a última parcela para zerar a diferença
                        const diff = diferencaParcelas();
                        const novas = [...parcelas];
                        const ultIdx = novas.length-1;
                        const novoVlr = (parseFloat(novas[ultIdx].vlrParcela||0) - diff).toFixed(2);
                        if (parseFloat(novoVlr) > 0) {
                          novas[ultIdx] = {...novas[ultIdx], vlrParcela: novoVlr};
                          setParcelas(novas);
                        }
                      }}>
                      <i className="bi bi-magic me-1"/>Ajustar última parcela
                    </button>
                  </div>
                )}

                {errors.parcelas&&<div className="alert alert-danger py-2 mb-3" style={{fontSize:13}}>{errors.parcelas}</div>}
                <table className="table table-custom">
                  <thead><tr><th>#</th><th>Vencimento</th><th>Valor</th><th>Conta Bancária</th><th>Status</th></tr></thead>
                  <tbody>
                    {parcelas.map((pc,i)=>(
                      <tr key={i}>
                        <td style={{fontWeight:700,color:"var(--primary)"}}>{i+1}</td>
                        <td>{isEdit?<span style={{fontSize:13}}>{pc.dtVencimento?new Date(pc.dtVencimento).toLocaleDateString("pt-BR"):"—"}</span>:<input type="date" className="form-control form-control-sm" value={pc.dtVencimento} onChange={e=>{const p=[...parcelas];p[i]={...p[i],dtVencimento:e.target.value};setParcelas(p);}}/>}</td>
                        <td>{isEdit?<span style={{fontSize:13,fontWeight:600}}>{fmtMoeda(pc.vlrParcela)}</span>:<div className="input-group input-group-sm"><span className="input-group-text">R$</span><input className="form-control" type="number" step="0.01" value={pc.vlrParcela} onChange={e=>{const p=[...parcelas];p[i]={...p[i],vlrParcela:e.target.value};setParcelas(p);}}/></div>}</td>
                        <td>{isEdit?<span style={{fontSize:12.5,color:"var(--gray-500)"}}>{pc.nmContaBancaria||"—"}</span>:<select className="form-select form-select-sm" value={pc.idContaBancaria} onChange={e=>{const p=[...parcelas];p[i]={...p[i],idContaBancaria:e.target.value};setParcelas(p);}}><option value="">— Selecione —</option>{contasBancarias.map(c=><option key={c.id} value={c.id}>{c.nmConta}</option>)}</select>}</td>
                        <td><span style={{background:pc.status==="Pago"?"#DCFCE7":pc.status==="Vencido"?"#FEE2E2":"#EFF6FF",color:pc.status==="Pago"?"#166534":pc.status==="Vencido"?"#991B1B":"var(--primary)",padding:"2px 8px",borderRadius:12,fontSize:12,fontWeight:600}}>{pc.status||"Em aberto"}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!isEdit&&<button className="btn btn-outline-secondary btn-sm mt-2" onClick={()=>setParcelas(p=>[...p,{dtVencimento:"",vlrParcela:"",idContaBancaria:""}])}><i className="bi bi-plus-lg"/> Adicionar parcela</button>}
              </div>
            )}
          </div>
          <div className="modal-footer d-flex justify-content-between">
            <div style={{fontSize:13,fontWeight:700,color:"var(--primary)"}}>Líquido: {fmtMoeda(vlrLiquido())}</div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={onClose}><i className="bi bi-x-circle"/> Cancelar</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?<><span className="spinner-border spinner-border-sm me-2"/>Salvando...</>:<><i className="bi bi-check-circle"/> {isEdit?"Salvar Alterações":"Lançar"}</>}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
